import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { axiosInstance, API } from "../App";
import { toast } from "sonner";
import { BarChart3, Package, Store, TrendingUp, LogOut, PlusCircle, FileText, Users } from "lucide-react";

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCounters: 0,
    totalProduk: 0,
    hariIni: 0,
    bulanIni: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Only fetch counters and produk for admin
      const promises = [axiosInstance.get(`${API}/nota`)];
      if (user?.role === "admin") {
        promises.push(axiosInstance.get(`${API}/counters`));
        promises.push(axiosInstance.get(`${API}/produk`));
      }

      const responses = await Promise.all(promises);
      const notaRes = responses[0];
      const countersRes = user?.role === "admin" ? responses[1] : null;
      const produkRes = user?.role === "admin" ? responses[2] : null;

      const today = new Date().toISOString().split("T")[0];
      const currentMonth = new Date().toISOString().slice(0, 7);

      // Filter nota by counter_id for non-admin users
      let filteredNota = notaRes.data;
      if (user?.role !== "admin" && user?.counter_id) {
        filteredNota = notaRes.data.filter((nota) => nota.counter_id === user.counter_id);
      }

      const todayTotal = filteredNota
        .filter((nota) => nota.tanggal === today)
        .reduce((sum, nota) => sum + (nota.total_nota || nota.total || 0), 0);

      const monthTotal = filteredNota
        .filter((nota) => nota.tanggal.startsWith(currentMonth))
        .reduce((sum, nota) => sum + (nota.total_nota || nota.total || 0), 0);

      setStats({
        totalCounters: countersRes ? countersRes.data.length : 0,
        totalProduk: produkRes ? produkRes.data.length : 0,
        hariIni: todayTotal,
        bulanIni: monthTotal,
      });
    } catch (error) {
      toast.error("Gagal memuat statistik");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate("/login");
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/logo-dumont.jpg" alt="Dumont" className="h-12 w-auto" />
              <div className="border-l border-gray-300 pl-3">
                <h1 className="text-2xl font-bold text-gray-800">Rekap Penjualan</h1>
                <p className="text-sm text-gray-600">Selamat datang, {user?.nama_lengkap}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {user?.role === "admin" ? "Admin" : "User"}
              </span>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="logout-button"
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Keluar</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard">
        {/* Stats Cards */}
        <div className={`grid grid-cols-1 ${user?.role === "admin" ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2"} gap-6 mb-8`}>
          {/* Only show Total Counter & Total Produk for Admin */}
          {user?.role === "admin" && (
            <>
              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Counter</CardTitle>
                    <Store className="w-5 h-5 text-blue-500" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-800" data-testid="total-counters">
                    {loading ? "..." : stats.totalCounters}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Produk</CardTitle>
                    <Package className="w-5 h-5 text-indigo-500" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-800" data-testid="total-produk">
                    {loading ? "..." : stats.totalProduk}
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">Penjualan Hari Ini</CardTitle>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-800" data-testid="hari-ini">
                {loading ? "..." : formatRupiah(stats.hariIni)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">Penjualan Bulan Ini</CardTitle>
                <BarChart3 className="w-5 h-5 text-purple-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-800" data-testid="bulan-ini">
                {loading ? "..." : formatRupiah(stats.bulanIni)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm mb-8">
          <CardHeader>
            <CardTitle className="text-xl">Menu Cepat</CardTitle>
            <CardDescription>Akses fitur utama dengan cepat</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                asChild
                data-testid="input-nota-button"
                className="h-20 flex flex-col items-center justify-center space-y-2 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md transition-all duration-200 hover:shadow-lg"
              >
                <Link to="/nota">
                  <PlusCircle className="w-6 h-6" />
                  <span className="font-medium">Input Nota</span>
                </Link>
              </Button>

              {user?.role === "admin" && (
                <>
                  <Button
                    asChild
                    data-testid="kelola-produk-button"
                    className="h-20 flex flex-col items-center justify-center space-y-2 bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white shadow-md transition-all duration-200 hover:shadow-lg"
                  >
                    <Link to="/produk">
                      <Package className="w-6 h-6" />
                      <span className="font-medium">Kelola Produk</span>
                    </Link>
                  </Button>

                  <Button
                    asChild
                    data-testid="kelola-counter-button"
                    className="h-20 flex flex-col items-center justify-center space-y-2 bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md transition-all duration-200 hover:shadow-lg"
                  >
                    <Link to="/counter">
                      <Store className="w-6 h-6" />
                      <span className="font-medium">Kelola Counter</span>
                    </Link>
                  </Button>

                  <Button
                    asChild
                    data-testid="kelola-user-button"
                    className="h-20 flex flex-col items-center justify-center space-y-2 bg-gradient-to-br from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white shadow-md transition-all duration-200 hover:shadow-lg"
                  >
                    <Link to="/users">
                      <Users className="w-6 h-6" />
                      <span className="font-medium">Kelola User</span>
                    </Link>
                  </Button>
                </>
              )}

              {/* Lihat Laporan - hanya untuk admin */}
              {user?.role === 'admin' && (
                <Button
                  asChild
                  data-testid="lihat-laporan-button"
                  className="h-20 flex flex-col items-center justify-center space-y-2 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-md transition-all duration-200 hover:shadow-lg"
                >
                  <Link to="/laporan">
                    <FileText className="w-6 h-6" />
                    <span className="font-medium">Lihat Laporan</span>
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
